import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import type { ShareLink, ShareLinkMinted } from './shareLinks'

const shareLinkKeys = { all: ['admin', 'share-links'] as const }

export function useShareLinks() {
  return useQuery({
    queryKey: shareLinkKeys.all,
    queryFn: async () => (await api.get<ShareLink[]>('/api/admin/share-links')).data,
  })
}

export function useCreateShareLink() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: { label: string; expires_in_days: number }) =>
      (await api.post<ShareLinkMinted>('/api/admin/share-links', body)).data,
    onSuccess: () => void client.invalidateQueries({ queryKey: shareLinkKeys.all }),
  })
}

export function useRevokeShareLink() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.post<ShareLink>(`/api/admin/share-links/${id}/revoke`)).data,
    onSuccess: () => void client.invalidateQueries({ queryKey: shareLinkKeys.all }),
  })
}

/** Plain fetch: the axios interceptor waits on a silent Microsoft sign-in first, which a fresh lobby browser never has. */
export async function openShareLink(token: string): Promise<void> {
  const response = await fetch(`${api.defaults.baseURL ?? ''}/api/auth/share`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  if (!response.ok) throw new Error(`share link refused: ${response.status}`)
}
